import { useState } from 'react';
import { ExternalLink, MoreHorizontal, RefreshCw, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useDomains, useDomainActions } from '@/hooks/useDispatcher';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { DomainDetailDrawer } from './DomainDetailDrawer';
import type { Domain } from '@/types/api';

const PAGE_SIZE = 50;

type DnsStatus = string | null | undefined;

function DnsPill({ status }: { status: DnsStatus }) {
  if (status === 'ok') return <Badge tone="green">{status}</Badge>;
  if (status === 'failed') return <Badge tone="red">{status}</Badge>;
  return <Badge tone="gray">pending</Badge>;
}

function ProviderBadge({ tag }: { tag: string | null }) {
  if (!tag) return <span className="text-gray-400">—</span>;
  const tone = tag.includes('instant') ? 'blue' : 'purple';
  return <Badge tone={tone}>{tag}</Badge>;
}

function WarmupBadge({ status }: { status: string | null | undefined }) {
  const s = status ?? 'cold';
  const tone = s === 'warm' ? 'green' : s === 'warming' ? 'yellow' : 'gray';
  return <Badge tone={tone}>{s}</Badge>;
}

export default function DomainsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';

  const [filters, setFilters] = useState({
    provider: '',
    country: '',
    status: '',
    warmup: '',
  });
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { data, isLoading } = useDomains({
    workspace_id: workspaceId,
    provider: filters.provider || undefined,
    country: filters.country || undefined,
    status: (filters.status as Domain['registration_status']) || undefined,
    warmup: filters.warmup || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const { recheckDns } = useDomainActions(workspaceId);

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageCount = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function setFilter(key: keyof typeof filters, value: string) {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  }

  async function handleRecheck(domain: Domain) {
    setOpenMenuId(null);
    await recheckDns.mutateAsync(domain.id);
    toast.success(`Recheck lancé pour ${domain.domain}`);
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          className="input-sm"
          value={filters.provider}
          onChange={(e) => setFilter('provider', e.target.value)}
        >
          <option value="">Tous les providers</option>
          <option value="successai">success.ai</option>
          <option value="instantly">Instantly</option>
        </select>
        <input
          type="text"
          className="input-sm"
          placeholder="Pays (FR, DE…)"
          value={filters.country}
          onChange={(e) => setFilter('country', e.target.value)}
        />
        <select
          className="input-sm"
          value={filters.status}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="ok">ok</option>
          <option value="pending">pending</option>
          <option value="suspended">suspended</option>
          <option value="retired">retired</option>
        </select>
        <select
          className="input-sm"
          value={filters.warmup}
          onChange={(e) => setFilter('warmup', e.target.value)}
        >
          <option value="">Tous les warmups</option>
          <option value="cold">cold</option>
          <option value="warming">warming</option>
          <option value="warm">warm</option>
        </select>
        {(filters.provider || filters.country || filters.status || filters.warmup) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilters({ provider: '', country: '', status: '', warmup: '' });
              setOffset(0);
            }}
          >
            Réinitialiser
          </Button>
        )}
        <span className="ml-auto text-sm text-gray-500">
          {total} domaine{total !== 1 ? 's' : ''}
        </span>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-48 w-full" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Aucun domaine trouvé" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-brand-border">
                <tr>
                  <th className="th">Domaine</th>
                  <th className="th">Provider</th>
                  <th className="th">Pays</th>
                  <th className="th">DKIM</th>
                  <th className="th">SPF</th>
                  <th className="th">DMARC</th>
                  <th className="th">Warmup</th>
                  <th className="th">Forwarder</th>
                  <th className="th">Comptes</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {items.map((domain) => (
                  <tr
                    key={domain.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(domain.id)}
                  >
                    <td className="td">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-brand-dark text-sm">
                          {domain.domain}
                        </span>
                        <a
                          href={`https://${domain.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-brand-primary"
                        >
                          <ExternalLink size={11} />
                        </a>
                      </div>
                    </td>
                    <td className="td">
                      <ProviderBadge tag={domain.provider_tag} />
                    </td>
                    <td className="td text-sm text-gray-500">{domain.country_iso ?? '—'}</td>
                    <td className="td">
                      <DnsPill status={domain.dkim_status} />
                    </td>
                    <td className="td">
                      <DnsPill status={domain.spf_status} />
                    </td>
                    <td className="td">
                      <DnsPill status={domain.dmarc_status} />
                    </td>
                    <td className="td">
                      <WarmupBadge status={domain.warmup_status} />
                    </td>
                    <td className="td">
                      {domain.ionos_forwarder_active ? (
                        <span className="text-green-600 text-sm">✓</span>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-brand-primary hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedId(domain.id);
                          }}
                        >
                          Configurer
                        </button>
                      )}
                    </td>
                    <td className="td text-sm text-gray-500">{domain.accounts_count ?? 0}</td>
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setOpenMenuId(openMenuId === domain.id ? null : domain.id)
                          }
                        >
                          <MoreHorizontal size={14} />
                        </Button>
                        {openMenuId === domain.id && (
                          <div className="absolute right-0 top-8 z-10 bg-white border border-brand-border rounded-lg shadow-lg py-1 w-44">
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => handleRecheck(domain)}
                            >
                              <RefreshCw size={12} /> Recheck DNS
                            </button>
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => {
                                setOpenMenuId(null);
                                setSelectedId(domain.id);
                              }}
                            >
                              <Settings size={12} /> Voir détail
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button
            size="sm"
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ‹ Précédent
          </Button>
          <span className="text-sm text-gray-500">
            {currentPage} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Suivant ›
          </Button>
        </div>
      )}

      <DomainDetailDrawer
        domainId={selectedId}
        workspaceId={workspaceId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
